// ============================================
// PAGE - Welcome / Complete Registration
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input } from '@almacenes/ui';
import logo from '@/assets/logos/logo.svg';
import { useRegiones, useComunas } from '@/hooks/useRegiones';
import { useCheckSignupStatus, useCompleteSignup } from '@almacenes/core';
import { useOnboardingOptions } from '@/hooks/useOnboardingOptions';

const TOTAL_STEPS = 4;

const steps = [
  { id: 1, title: 'Tu almacén' },
  { id: 2, title: 'Datos acceso' },
  { id: 3, title: 'Información general' },
  { id: 4, title: 'Últimos detalles' },
];

export const WelcomePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showWelcome, setShowWelcome] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [, setSessionData] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState(1);
  
  // Obtener parámetros de la URL
  const userId = searchParams.get('user');
  const almacenId = searchParams.get('almacen');

  // Validar estado de registro (se desactiva tras completar para no re-consultar
  // un almacen que el propio complete ya marco como activo, lo que produciria
  // un error de "registro ya completado" pisando la pantalla de exito)
  const { data: statusData, isLoading, error } = useCheckSignupStatus(userId, almacenId, {
    enabled: !showSuccess,
  });
  
  // Mutación para completar registro
  const { mutate: completeSignup, isPending: isSubmitting } = useCompleteSignup();
  
  // State para manejar errores de submit
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Redirigir al login si no vienen los parámetros
  useEffect(() => {
    if (!userId || !almacenId) {
      navigate('/auth/login', { replace: true });
    }
  }, [userId, almacenId, navigate]);
  
  // State para los datos del formulario
  const [formData, setFormData] = useState({
    // Paso 1: Datos del almacén
    nombreAlmacen: '',
    direccion: '',
    numeroDireccion: '',
    local: '',
    codigoRegion: null as number | null,
    idComuna: null as number | null,
    // Paso 2: Administrador/Vendedor
    claveAdministrador: '',
    claveAdministradorConfirm: '',
    nombreVendedor: '',
    claveVendedor: '',
    // Paso 3: Información general
    patenteAlcohol: null as boolean | null,
    tieneFrios: null as boolean | null,
    tieneCongelados: null as boolean | null,
    ventaPorMayor: null as boolean | null,
    tamanoLocal: '',
    tipoAlmacen: '',
    // Paso 4: SII (Opcional)
    claveSII: '',
    claveSIIConfirm: '',
    // Paso 5: Términos y condiciones
    aceptaTerminos: false,
  });

  // Validación de cada paso
  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return (
          formData.nombreAlmacen.trim() !== '' &&
          formData.direccion.trim() !== '' &&
          formData.numeroDireccion.trim() !== '' &&
          formData.codigoRegion !== null &&
          formData.idComuna !== null
        );
      case 2:
        return (
          formData.claveAdministrador.trim() !== '' &&
          formData.claveAdministrador.length >= 8 &&
          formData.claveAdministradorConfirm.trim() !== '' &&
          formData.claveAdministrador === formData.claveAdministradorConfirm &&
          formData.nombreVendedor.trim() !== '' &&
          formData.claveVendedor.trim() !== '' &&
          formData.claveVendedor.length >= 8
        );
      case 3:
        return (
          formData.patenteAlcohol !== null &&
          formData.tieneFrios !== null &&
          formData.tieneCongelados !== null &&
          formData.ventaPorMayor !== null &&
          formData.tamanoLocal.trim() !== '' &&
          formData.tipoAlmacen.trim() !== ''
        );
      case 4:
        // Validar términos - SII es opcional pero si ingresa clave debe confirmarla
        const siiValid = formData.claveSII.trim() === '' || formData.claveSII === formData.claveSIIConfirm;
        return formData.aceptaTerminos && siiValid;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    } else {
      // Último paso - completar registro
      if (!userId || !almacenId || !statusData) return;
      
      completeSignup(
        {
          usuarioId: statusData.usuarioId,
          almacenId: statusData.almacenId,
          nombreComercial: formData.nombreAlmacen,
          direccion: formData.direccion,
          numeroDireccion: parseInt(formData.numeroDireccion, 10),
          local: formData.local.trim() || null,
          comunaId: formData.idComuna!,
          tienePatenteAlcohol: formData.patenteAlcohol!,
          tieneFrios: formData.tieneFrios!,
          tieneCongelados: formData.tieneCongelados!,
          vendePorMayor: formData.ventaPorMayor!,
          tamanoLocal: formData.tamanoLocal,
          tipoAlmacen: formData.tipoAlmacen,
          claveSii: formData.claveSII.trim() || null,
          clavePropietario: formData.claveAdministrador,
          nombreVendedor: formData.nombreVendedor,
          claveVendedor: formData.claveVendedor,
        },
        {
          onSuccess: (data: any) => {
            // Guardar datos de sesión y mostrar pantalla de éxito
            setSessionData(data);
            setShowSuccess(true);
          },
          onError: (error: any) => {
            const message = error?.response?.data?.message || error?.message || 'Error al completar el registro';
            setErrorMessage(message);
            setShowErrorModal(true);
          },
        }
      );
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveAndExit = () => {
    // Guardar progreso y salir
    navigate('/auth/login');
  };

  // Si hay error de validación (409), mostrar pantalla de error
  // (nunca si el registro ya se completo con exito en esta misma sesion)
  if (error && !showSuccess) {
    const apiError = error as any;
    // Intentar obtener el mensaje del error desde diferentes estructuras posibles
    const errorMessage = 
      apiError?.response?.data?.message || 
      apiError?.message || 
      'Error al verificar el estado de registro';

    return (
      <div className="min-h-dvh h-dvh flex flex-col overflow-hidden">
        <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center">
          <img 
            src={logo} 
            alt="Almacenes Digitales" 
            className="h-9"
          />
        </div>

        <div className="flex-1 flex items-start md:items-center justify-center overflow-auto px-8 py-8" style={{ backgroundColor: '#F3F4F9' }}>
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="mb-6">
                <svg className="w-16 h-16 mx-auto text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-title-3 mb-4">No puedes continuar</h2>
              <p className="text-body text-gray-600 mb-6">{errorMessage}</p>
              <Button
                variant="primary"
                onClick={() => navigate('/auth/login')}
                className="w-full"
              >
                Volver al Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mostrar loading mientras se valida
  if (!showSuccess && (isLoading || !statusData)) {
    return (
      <div className="min-h-dvh h-dvh flex flex-col overflow-hidden">
        <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center">
          <img 
            src={logo} 
            alt="Almacenes Digitales" 
            className="h-9"
          />
        </div>

        <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: '#F3F4F9' }}>
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" style={{ borderTopColor: '#43BFFF', borderRightColor: 'transparent', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
            <p className="mt-4 text-body text-gray-600">Verificando información...</p>
          </div>
        </div>
      </div>
    );
  }

  // Pantalla de éxito
  if (showSuccess) {
    const nombreRaw = statusData?.nombreUsuario?.split(' ')[0] || 'Usuario';
    const primerNombre = nombreRaw.charAt(0).toUpperCase() + nombreRaw.slice(1).toLowerCase();

    return (
      <div className="min-h-dvh h-dvh flex flex-col overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0e1f3d 25%, #1a3060 50%, #0e1f3d 75%, #0a1628 100%)' }}>
        {/* Topbar simple */}
        <div className="bg-white/10 backdrop-blur-sm border-b border-white/20 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center">
            <h2 className="text-xl font-bold flex items-baseline text-white">
              <span>ALMACENES</span>
              {' '}
              <span className="text-[#43BFFF] text-lg font-normal">digitales</span>
            </h2>
          </div>
        </div>

        {/* Contenido principal */}
        <div className="flex-1 flex items-start md:items-center justify-center overflow-auto px-4 py-8">
          <div className="w-full max-w-2xl">
            <div className="bg-white/10 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12 border border-white/20">
              {/* Ícono de éxito */}
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-5xl font-bold shadow-lg">
                  🎉
                </div>
              </div>

              {/* Título */}
              <h1 className="text-3xl md:text-4xl font-bold text-white text-center mb-3">
                ¡Tu almacén está listo!
              </h1>
              
              <p className="text-white/90 text-center text-lg mb-4">
                Configuraste todo en tiempo récord, {primerNombre}. Estos son tus primeros 3 pasos para sacarle el máximo provecho.
              </p>

              {/* Mensaje de Premium si no es almacén válido */}

              <div className="mb-8 p-4 rounded-lg" style={{ backgroundColor: '#F0FDF4', border: '1px solid #86EFAC' }}>
                <p className="text-sm font-medium" style={{ color: '#15803D' }}>
                  ✨ Acceso Premium activado — Prueba todas las funcionalidades ✨
                </p>
              </div>


              {/* Próximos pasos */}
              <div className="space-y-3 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold mb-1">Agrega tus primeros productos</h3>
                    <p className="text-white/70 text-sm">Carga tu inventario para poder registrar ventas</p>
                  </div>
                  <svg className="w-6 h-6 text-white/60 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold mb-1">Registra tu primera venta</h3>
                    <p className="text-white/70 text-sm">Ve cómo funciona el punto de venta</p>
                  </div>
                  <svg className="w-6 h-6 text-white/60 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold mb-1">Activa la boleta electrónica</h3>
                    <p className="text-white/70 text-sm">Gratis — solo necesitas tu clave SII</p>
                  </div>
                  <svg className="w-6 h-6 text-white/60 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Botón ir al panel */}
              <div className="flex justify-center">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-green-500 hover:bg-green-600 text-white font-semibold px-16 py-3 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl"
                >
                  🚀 Ir a mi panel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Pantalla de bienvenida
  if (showWelcome) {
    const nombreRaw = statusData?.nombreUsuario?.split(' ')[0] || 'Usuario';
    const primerNombre = nombreRaw.charAt(0).toUpperCase() + nombreRaw.slice(1).toLowerCase();
    const iniciales = statusData?.nombreUsuario
      ?.split(' ')
      .slice(0, 2)
      .map(n => n[0])
      .join('')
      .toUpperCase() || 'U';

    return (
      <div className="min-h-dvh h-dvh flex flex-col overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0e1f3d 25%, #1a3060 50%, #0e1f3d 75%, #0a1628 100%)' }}>
        {/* Topbar con pasos */}
        <div className="bg-white/10 backdrop-blur-sm border-b border-white/20 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Logo izquierda */}
            <div className="flex-1">
              <h2 className="text-xl font-bold flex items-baseline text-white">
                <span>ALMACENES</span>
                {' '}
                <span className="text-[#43BFFF] text-lg font-normal">digitales</span>
              </h2>
            </div>
            
            {/* Pasos centrados (ocultos en mobile: no caben junto al logo y "Salir") */}
            <div className="hidden md:flex items-center gap-6">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        step.id < currentStep
                          ? 'bg-green-500 text-white'
                          : 'bg-white/20 text-white'
                      }`}
                    >
                      {step.id < currentStep ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        step.id
                      )}
                    </div>
                    <span
                      className={`text-sm hidden md:inline ${
                        step.id < currentStep
                          ? 'text-green-400 font-medium'
                          : 'text-white'
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-8 h-0.5 hidden lg:block ${
                        step.id < currentStep ? 'bg-green-400' : 'bg-white/20'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Salir derecha */}
            <div className="flex-1 flex justify-end">
              <button
                onClick={() => navigate('/auth/login')}
                className="text-red-500 hover:text-red-400 text-sm font-medium transition-colors"
              >
                Salir
              </button>
            </div>
          </div>
        </div>

        {/* Contenido principal */}
        <div className="flex-1 flex items-start md:items-center justify-center overflow-auto px-4 py-8">
          <div className="w-full max-w-2xl">
            <div className="bg-white/10 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12 border border-white/20">
              {/* Avatar */}
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                  {iniciales}
                </div>
              </div>

              {/* Título */}
              <h1 className="text-3xl md:text-4xl font-bold text-white text-center mb-3">
                Hola {primerNombre}, bienvenido 🎉
              </h1>
              
              <p className="text-white/90 text-center text-lg mb-8">
                Tu cuenta está verificada. Solo necesitas 3 minutos para configurar tu almacén y empezar a usarlo.
              </p>

              {/* Módulos disponibles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 border border-white/20 flex items-center gap-2">
                  <div className="text-2xl">📦</div>
                  <p className="text-white text-xs font-medium">Inventario</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 border border-white/20 flex items-center gap-2">
                  <div className="text-2xl">💰</div>
                  <p className="text-white text-xs font-medium">Ventas</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 border border-white/20 flex items-center gap-2">
                  <div className="text-2xl">🧾</div>
                  <p className="text-white text-xs font-medium">Boleta gratis</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 border border-white/20 flex items-center gap-2">
                  <div className="text-2xl">📊</div>
                  <p className="text-white text-xs font-medium">Reportes</p>
                </div>
              </div>

              {/* Botón comenzar */}
              <div className="flex justify-center">
                <Button
                  variant="primary"
                  onClick={() => setShowWelcome(false)}
                  className="w-full md:w-auto px-6 md:px-16 text-lg py-3"
                >
                  Comenzar configuración →
                </Button>
              </div>
              
              <p className="text-white/70 text-center text-sm mt-4">
                ⏱ Proceso de 4 pasos · Aproximadamente 3 minutos
              </p>
            </div>
          </div>
        </div>

      </div>
    );
  }

  return (
    <div className="min-h-dvh h-dvh flex flex-col overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0e1f3d 25%, #1a3060 50%, #0e1f3d 75%, #0a1628 100%)' }}>
      {/* Topbar con pasos */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo izquierda */}
          <div className="flex-1">
            <h2 className="text-xl font-bold flex items-baseline text-white">
              <span>ALMACENES</span>
              {' '}
              <span className="text-[#43BFFF] text-lg font-normal">digitales</span>
            </h2>
          </div>
          
          {/* Pasos centrados (ocultos en mobile: no caben junto al logo y "Salir") */}
          <div className="hidden md:flex items-center gap-6">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      step.id === currentStep
                        ? 'bg-white text-blue-900'
                        : step.id < currentStep
                        ? 'bg-green-500 text-white'
                        : 'bg-white/20 text-white'
                    }`}
                  >
                    {step.id < currentStep ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      step.id
                    )}
                  </div>
                  <span
                    className={`text-sm hidden md:inline transition-colors ${
                      step.id === currentStep
                        ? 'text-white font-medium'
                        : step.id < currentStep
                        ? 'text-green-400 font-medium'
                        : 'text-white/60'
                    }`}
                  >
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-8 h-0.5 hidden lg:block ${
                      step.id < currentStep ? 'bg-green-400' : 'bg-white/20'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Salir derecha */}
          <div className="flex-1 flex justify-end">
            <button
              onClick={handleSaveAndExit}
              className="text-red-500 hover:text-red-400 text-sm font-medium transition-colors"
            >
              Salir
            </button>
          </div>
        </div>
      </div>

      {/* Contenido principal (con su propio scroll; los botones de accion viven en el footer fijo de abajo, no aqui) */}
      <div className="flex-1 overflow-auto px-4 py-6 md:py-8">
        <div className="w-full max-w-4xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex gap-2">
              {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
                <div
                  key={index}
                  className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                    index < currentStep - 1
                      ? 'bg-green-500'
                      : index === currentStep - 1
                      ? 'bg-white/30'
                      : 'bg-white/30'
                  }`}
                  style={
                    index === currentStep - 1
                      ? { backgroundColor: '#3B9EFF' }
                      : undefined
                  }
                />
              ))}
            </div>
          </div>

          {/* Card del formulario */}
          <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-10">
            <div className="text-left mb-4">
              <span className="text-lg font-semibold text-[#3B9EFF]">
                Paso {currentStep} de {TOTAL_STEPS}
              </span>
            </div>
            {/* Step Content */}
            <div>
              {currentStep === 1 && <StepAlmacen formData={formData} setFormData={setFormData} />}
              {currentStep === 2 && <StepAdministrador formData={formData} setFormData={setFormData} />}
              {currentStep === 3 && <StepInformacionGeneral formData={formData} setFormData={setFormData} />}
              {currentStep === 4 && <StepTerminos formData={formData} setFormData={setFormData} />}
            </div>

            {/* Action Buttons (desktop): en pantallas grandes el contenido casi siempre cabe,
                asi que los botones vuelven a vivir dentro del card, pegados al contenido */}
            <div className="hidden md:flex items-center justify-between gap-3 mt-8">
              <Button
                variant="neutral"
                onClick={handleBack}
                disabled={currentStep === 1}
                className="px-8"
              >
                Atrás
              </Button>

              <Button
                variant={currentStep === TOTAL_STEPS ? 'success' : 'primary'}
                onClick={handleNext}
                className={`px-8 ${currentStep === TOTAL_STEPS ? 'bg-green-500 hover:bg-green-600 text-white' : ''}`}
                disabled={!isStepValid() || isSubmitting}
                isLoading={isSubmitting}
              >
                {currentStep === TOTAL_STEPS ? 'Comenzar' : 'Siguiente'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons (mobile): footer fijo fuera del area con scroll, siempre visible
          sin importar cuanto mida el paso actual. Oculto desde md porque ahi los botones
          ya viven dentro del card de arriba. */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 md:hidden">
        <div className="w-full flex flex-col gap-3">
          <Button
            variant={currentStep === TOTAL_STEPS ? 'success' : 'primary'}
            onClick={handleNext}
            className={`px-8 w-full order-1 ${currentStep === TOTAL_STEPS ? 'bg-green-500 hover:bg-green-600 text-white' : ''}`}
            disabled={!isStepValid() || isSubmitting}
            isLoading={isSubmitting}
          >
            {currentStep === TOTAL_STEPS ? 'Comenzar' : 'Siguiente'}
          </Button>

          <Button
            variant="neutral"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="px-8 w-full order-2"
          >
            Atrás
          </Button>
        </div>
      </div>

      {/* Modal de error */}
      {showErrorModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowErrorModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#11194F' }}>
                Error al completar registro
              </h2>
              <p className="text-gray-700 mb-6">{errorMessage}</p>
              <Button
                variant="primary"
                onClick={() => setShowErrorModal(false)}
                className="w-full"
              >
                Entendido
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// Step Components
// ============================================

interface StepAlmacenProps {
  formData: {
    nombreAlmacen: string;
    direccion: string;
    numeroDireccion: string;
    local: string;
    codigoRegion: number | null;
    idComuna: number | null;
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}

const StepAlmacen = ({ formData, setFormData }: StepAlmacenProps) => {
  const { data: regiones, isLoading: isLoadingRegiones } = useRegiones();
  const { data: comunas, isLoading: isLoadingComunas } = useComunas(formData.codigoRegion);

  return (
    <div>
      <h2 className="text-title-3 mb-2">Datos del almacén</h2>
      <p className="text-body-secondary mb-6">
        Ingresa la información básica de tu almacén.
      </p>

      <div className="space-y-4">
        <Input
          label="Nombre del almacén"
          type="text"
          placeholder="Nombre de tu negocio"
          required
          value={formData.nombreAlmacen}
          onChange={(e) => setFormData({ ...formData, nombreAlmacen: e.target.value })}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Input
              label="Dirección"
              type="text"
              placeholder="Calle o avenida"
              required
              value={formData.direccion}
              onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
            />
          </div>

          <Input
            label="Número"
            type="text"
            placeholder="Número"
            required
            value={formData.numeroDireccion}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setFormData({ ...formData, numeroDireccion: value });
            }}
          />
        </div>

        <Input
          label="Local / Depto (opcional)"
          type="text"
          placeholder="Local, depto, oficina, etc."
          value={formData.local}
          onChange={(e) => setFormData({ ...formData, local: e.target.value })}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Región
            </label>
            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base"
              required
              value={formData.codigoRegion || ''}
              onChange={(e) => {
                const codigoRegion = e.target.value ? parseInt(e.target.value, 10) : null;
                setFormData({ 
                  ...formData, 
                  codigoRegion,
                  idComuna: null // Reset comuna cuando cambie región
                });
              }}
              disabled={isLoadingRegiones}
            >
              <option value="">Selecciona una región</option>
              {regiones?.map((region) => (
                <option key={region.codigo} value={region.codigo}>
                  {region.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comuna
            </label>
            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
              value={formData.idComuna || ''}
              onChange={(e) => {
                const idComuna = e.target.value ? parseInt(e.target.value, 10) : null;
                setFormData({ ...formData, idComuna });
              }}
              disabled={!formData.codigoRegion || isLoadingComunas}
            >
              <option value="">
                {!formData.codigoRegion 
                  ? 'Primero selecciona una región' 
                  : 'Selecciona una comuna'}
              </option>
              {comunas?.map((comuna) => (
                <option key={comuna.id} value={comuna.id}>
                  {comuna.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

const StepAdministrador = ({ formData, setFormData }: any) => {
  const [errors, setErrors] = useState({ 
    claveAdministrador: '',
    claveVendedor: ''
  });

  const handleClaveAdministradorConfirmChange = (value: string) => {
    setFormData({ ...formData, claveAdministradorConfirm: value });
    
    // Validar que las claves coincidan
    if (value && formData.claveAdministrador && value !== formData.claveAdministrador) {
      setErrors({ ...errors, claveAdministrador: 'Las claves no coinciden' });
    } else {
      setErrors({ ...errors, claveAdministrador: '' });
    }
  };

  return (
    <div>
      <h2 className="text-title-3 mb-2">Datos del administrador/vendedor</h2>
      <p className="text-body-secondary mb-6">
        Configura las credenciales para el administrador y vendedor.
      </p>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Crear clave del administrador"
              type="password"
              placeholder="Mínimo 8 caracteres"
              required
              value={formData.claveAdministrador}
              onChange={(e) => {
                setFormData({ ...formData, claveAdministrador: e.target.value });
                // Validar longitud mínima
                if (e.target.value && e.target.value.length < 8) {
                  setErrors({ ...errors, claveAdministrador: 'La clave debe tener al menos 8 caracteres' });
                } else if (formData.claveAdministradorConfirm && e.target.value !== formData.claveAdministradorConfirm) {
                  setErrors({ ...errors, claveAdministrador: 'Las claves no coinciden' });
                } else {
                  setErrors({ ...errors, claveAdministrador: '' });
                }
              }}
              className={errors.claveAdministrador ? 'border-red-500' : ''}
            />
            {errors.claveAdministrador && formData.claveAdministrador.length > 0 && formData.claveAdministrador.length < 8 && (
              <p className="mt-1 text-sm text-red-600">{errors.claveAdministrador}</p>
            )}
          </div>

          <div>
            <Input
              label="Repite la clave del administrador"
              type="password"
              placeholder="Repite la clave"
              required
              value={formData.claveAdministradorConfirm}
              onChange={(e) => handleClaveAdministradorConfirmChange(e.target.value)}
              className={errors.claveAdministrador ? 'border-red-500' : ''}
            />
            {errors.claveAdministrador && (
              <p className="mt-1 text-sm text-red-600">{errors.claveAdministrador}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Nombre del vendedor"
            type="text"
            placeholder="Nombre completo del vendedor"
            required
            value={formData.nombreVendedor}
            onChange={(e) => setFormData({ ...formData, nombreVendedor: e.target.value })}
          />

          <div>
            <Input
              label="Crear clave del vendedor"
              type="password"
              placeholder="Mínimo 8 caracteres"
              required
              value={formData.claveVendedor}
              onChange={(e) => {
                setFormData({ ...formData, claveVendedor: e.target.value });
                // Validar longitud mínima
                if (e.target.value && e.target.value.length < 8) {
                  setErrors({ ...errors, claveVendedor: 'La clave debe tener al menos 8 caracteres' });
                } else {
                  setErrors({ ...errors, claveVendedor: '' });
                }
              }}
              className={errors.claveVendedor ? 'border-red-500' : ''}
            />
            {errors.claveVendedor && formData.claveVendedor.length > 0 && formData.claveVendedor.length < 8 && (
              <p className="mt-1 text-sm text-red-600">{errors.claveVendedor}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StepInformacionGeneral = ({ formData, setFormData }: any) => {
  const { data: onboardingOptions, isLoading: isLoadingOptions } = useOnboardingOptions();
  const [isOtroSelected, setIsOtroSelected] = useState(false);
  const [isOtroSelectSelected, setIsOtroSelectSelected] = useState(false);
  const [tipoAlmacenPersonalizado, setTipoAlmacenPersonalizado] = useState('');

  return (
    <div>
      <h2 className="text-title-3 mb-2">Información general</h2>
      <p className="text-body-secondary mb-6">
        Información adicional sobre tu almacén.
      </p>

      <div className="space-y-4">
        {/* Primera fila: 4 preguntas de Sí/No */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ¿Tienes patente de Alcohol?
            </label>
            <div className="flex max-w-xs">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, patenteAlcohol: true })}
                className={`w-16 px-3 py-2 rounded-l-lg text-sm font-medium transition-all border ${
                  formData.patenteAlcohol === true
                    ? 'bg-[#1e3a8a] text-white border-[#1e3a8a] z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Sí
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, patenteAlcohol: false })}
                className={`w-16 px-3 py-2 rounded-r-lg text-sm font-medium transition-all border ${
                  formData.patenteAlcohol === false
                    ? 'bg-[#1e3a8a] text-white border-[#1e3a8a] z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 -ml-px'
                }`}
              >
                No
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ¿Tienes Fríos?
            </label>
            <div className="flex max-w-xs">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, tieneFrios: true })}
                className={`w-16 px-3 py-2 rounded-l-lg text-sm font-medium transition-all border ${
                  formData.tieneFrios === true
                    ? 'bg-[#1e3a8a] text-white border-[#1e3a8a] z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Sí
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, tieneFrios: false })}
                className={`w-16 px-3 py-2 rounded-r-lg text-sm font-medium transition-all border ${
                  formData.tieneFrios === false
                    ? 'bg-[#1e3a8a] text-white border-[#1e3a8a] z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 -ml-px'
                }`}
              >
                No
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ¿Tienes Congelados?
            </label>
            <div className="flex max-w-xs">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, tieneCongelados: true })}
                className={`w-16 px-3 py-2 rounded-l-lg text-sm font-medium transition-all border ${
                  formData.tieneCongelados === true
                    ? 'bg-[#1e3a8a] text-white border-[#1e3a8a] z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Sí
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, tieneCongelados: false })}
                className={`w-16 px-3 py-2 rounded-r-lg text-sm font-medium transition-all border ${
                  formData.tieneCongelados === false
                    ? 'bg-[#1e3a8a] text-white border-[#1e3a8a] z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 -ml-px'
                }`}
              >
                No
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ¿Vendes al por mayor?
            </label>
            <div className="flex max-w-xs">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, ventaPorMayor: true })}
                className={`w-16 px-3 py-2 rounded-l-lg text-sm font-medium transition-all border ${
                  formData.ventaPorMayor === true
                    ? 'bg-[#1e3a8a] text-white border-[#1e3a8a] z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Sí
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, ventaPorMayor: false })}
                className={`w-16 px-3 py-2 rounded-r-lg text-sm font-medium transition-all border ${
                  formData.ventaPorMayor === false
                    ? 'bg-[#1e3a8a] text-white border-[#1e3a8a] z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 -ml-px'
                }`}
              >
                No
              </button>
            </div>
          </div>
        </div>

        {/* Segunda fila: Tamaño del local + Tipo de almacén */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ¿Qué tamaño es tu local?
            </label>
            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base"
              required
              value={formData.tamanoLocal}
              onChange={(e) => setFormData({ ...formData, tamanoLocal: e.target.value })}
            >
              <option value="">Selecciona una opción</option>
              <option value="0-10 metros cuadrados">0-10 metros cuadrados</option>
              <option value="11-50 metros cuadrados">11-50 metros cuadrados</option>
              <option value="51-100 metros cuadrados">51-100 metros cuadrados</option>
              <option value="101-150 metros cuadrados">101-150 metros cuadrados</option>
              <option value="mas de 150 metros cuadrados">mas de 150 metros cuadrados</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de almacén
            </label>
            
            {/* Mobile: cuadricula 2x2 de tarjetas independientes (4 nombres largos no caben en una fila) */}
            {/* Desktop (md+): las mismas 4 opciones como botones unidos en una fila */}
            <div className="grid grid-cols-2 gap-2 md:flex md:gap-0">
              {onboardingOptions?.storeTypes?.slice(0, 3).map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, tipoAlmacen: option.value });
                    setIsOtroSelected(false);
                  }}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-all border rounded-lg ${
                    index === 0 ? 'md:rounded-l-lg md:rounded-r-none' : 'md:rounded-none md:-ml-px'
                  } ${
                    formData.tipoAlmacen === option.value && !isOtroSelected
                      ? 'bg-[#1e3a8a] text-white border-[#1e3a8a] z-10'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setIsOtroSelected(true);
                  // Limpiar el valor si era una de las primeras 3 opciones
                  const isInFirstThree = onboardingOptions?.storeTypes?.slice(0, 3).some(opt => opt.value === formData.tipoAlmacen);
                  if (isInFirstThree) {
                    setFormData({ ...formData, tipoAlmacen: '' });
                  }
                }}
                className={`flex-1 px-4 py-3 rounded-lg md:rounded-l-none md:rounded-r-lg text-sm font-medium transition-all border md:-ml-px ${
                  isOtroSelected
                    ? 'bg-[#1e3a8a] text-white border-[#1e3a8a] z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Otro
              </button>
            </div>
          </div>
        </div>

        {/* Tercera fila: Opciones adicionales cuando selecciona "Otro" */}
        {isOtroSelected && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selecciona el tipo
              </label>
              <select
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-base"
                required
                value={isOtroSelectSelected ? 'otro' : formData.tipoAlmacen}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'otro') {
                    setIsOtroSelectSelected(true);
                    setFormData({ ...formData, tipoAlmacen: '' });
                  } else {
                    setIsOtroSelectSelected(false);
                    setTipoAlmacenPersonalizado('');
                    setFormData({ ...formData, tipoAlmacen: value });
                  }
                }}
                disabled={isLoadingOptions}
              >
                <option value="">Selecciona una opción</option>
                {onboardingOptions?.storeTypes?.slice(3).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                <option value="otro">Otro</option>
              </select>
            </div>

            {/* Input para tipo de almacén personalizado (solo si seleccionó "Otro" en el select) */}
            {isOtroSelectSelected && (
              <div>
                <Input
                  label="Especifica el tipo de almacén"
                  type="text"
                  placeholder="Ingresa el tipo de almacén"
                  required
                  value={tipoAlmacenPersonalizado}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTipoAlmacenPersonalizado(value);
                    setFormData({ ...formData, tipoAlmacen: value });
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StepTerminos = ({ formData, setFormData }: any) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [errors, setErrors] = useState({ claveSII: '' });

  const handleClaveSIIConfirmChange = (value: string) => {
    setFormData({ ...formData, claveSIIConfirm: value });
    
    // Validar que las claves coincidan si hay algo en claveSII
    if (formData.claveSII && value !== formData.claveSII) {
      setErrors({ claveSII: 'Las claves no coinciden' });
    } else {
      setErrors({ claveSII: '' });
    }
  };

  return (
    <div className="text-center">
      <h2 className="text-title-3 mb-2">¡Excelente! ya estás en el último paso.</h2>
      <p className="text-body text-gray-600 mb-6">
        Lee los términos y confirma. Las demás configuraciones las puedes hacer después.
      </p>

      <div className="mt-6 flex justify-center">
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 max-w-2xl w-full">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="terms"
              className="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              checked={formData.aceptaTerminos}
              onChange={(e) => setFormData({ ...formData, aceptaTerminos: e.target.checked })}
            />
            <label htmlFor="terms" className="text-sm text-gray-700">
              Aceptar{' '}
              <a
                href="https://almacenesdigitales.cl/terminosCondicionesPoliticaPrivacidad.xhtml"
                target="_blank"
                rel="noopener noreferrer"
                className="text-link hover:underline"
              >
                Términos, Condiciones y Políticas de Privacidad
              </a>
              {' '}de Almacenes Digitales.
            </label>
          </div>
        </div>
      </div>

      {/* Configuración Opcional del SII */}
      <hr className="my-8 border-gray-300" />
      
      <div className="mt-6">
        <p className="text-base font-medium text-gray-700 mb-4 text-left">Configuración opcional (puedes hacerlo después)</p>
        
        <div className="border border-gray-300 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setIsAccordionOpen(!isAccordionOpen)}
            className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
          >
            <span className="text-sm font-medium text-gray-700">
              Clave del SII - para boleta electrónica
            </span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${isAccordionOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isAccordionOpen && (
            <div className="p-4 bg-white border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-4 text-left">
                ¡Agrega la clave del SII y obtén un resumen de tus movimientos! 📊
              </p>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Clave SII"
                    type="password"
                    placeholder="Ingresa tu clave del SII"
                    value={formData.claveSII}
                    onChange={(e) => {
                      setFormData({ ...formData, claveSII: e.target.value });
                      // Revalidar si ya hay algo en el confirm
                      if (formData.claveSIIConfirm && e.target.value !== formData.claveSIIConfirm) {
                        setErrors({ claveSII: 'Las claves no coinciden' });
                      } else {
                        setErrors({ claveSII: '' });
                      }
                    }}
                  />

                  <div>
                    <Input
                      label="Repetir Clave"
                      type="password"
                      placeholder="Repite tu clave del SII"
                      value={formData.claveSIIConfirm}
                      onChange={(e) => handleClaveSIIConfirmChange(e.target.value)}
                      className={errors.claveSII ? 'border-red-500' : ''}
                      disabled={!formData.claveSII}
                    />
                    {errors.claveSII && (
                      <p className="mt-1 text-sm text-red-600">{errors.claveSII}</p>
                    )}
                  </div>
                </div>

                <p className="text-xs text-gray-600 italic text-left">
                  Nota: La clave del SII es opcional y puedes configurarla más tarde desde tu panel de administración.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
